import React, { useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from 'recharts';
import useGameStore from '../engine/store';

function formatMoney(value) {
  if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (Math.abs(value) >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${Math.round(value)}`;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-dark-800/95 border border-white/10 rounded-lg p-3 shadow-xl text-xs">
        <p className="text-gray-400 mb-1">Month {label}</p>
        {payload.map((p, i) => (
          <p key={i} className="font-mono" style={{ color: p.color }}>
            {p.name}: {formatMoney(p.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

function CapitalChart() {
  const { monthlyHistory } = useGameStore();

  const data = monthlyHistory.map(m => ({
    month: m.month,
    capital: m.capital,
    liquidity: m.liquidity,
  }));

  if (data.length < 2) return null;

  return (
    <div className="glass-panel p-4">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Capital & Liquidity</h3>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="capGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#00aaff" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#00aaff" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="liqGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#00ff88" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#00ff88" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
          <XAxis dataKey="month" stroke="#4b5563" tick={{ fontSize: 10 }} />
          <YAxis stroke="#4b5563" tick={{ fontSize: 10 }} tickFormatter={formatMoney} />
          <Tooltip content={<CustomTooltip />} />
          <Area type="monotone" dataKey="capital" stroke="#00aaff" fill="url(#capGrad)" strokeWidth={2} name="Capital" />
          <Area type="monotone" dataKey="liquidity" stroke="#00ff88" fill="url(#liqGrad)" strokeWidth={1.5} name="Liquidity" strokeDasharray="4 2" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function ProfitLossChart() {
  const { monthlyHistory } = useGameStore();

  const data = monthlyHistory.map(m => ({
    month: m.month,
    income: m.income,
    losses: -m.losses,
    profit: m.profit,
  }));

  if (data.length < 2) return null;

  return (
    <div className="glass-panel p-4">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Monthly P&L</h3>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
          <XAxis dataKey="month" stroke="#4b5563" tick={{ fontSize: 10 }} />
          <YAxis stroke="#4b5563" tick={{ fontSize: 10 }} tickFormatter={formatMoney} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="income" fill="#00ff88" name="Income" radius={[2, 2, 0, 0]} />
          <Bar dataKey="losses" fill="#ff3366" name="Losses" radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function PortfolioPie() {
  const { activeLoans } = useGameStore();

  const data = useMemo(() => {
    const types = { individual: 0, business: 0, startup: 0 };
    for (const loan of activeLoans) {
      types[loan.borrowerType] = (types[loan.borrowerType] || 0) + loan.remainingPrincipal;
    }
    return [
      { name: 'Individual', value: types.individual, color: '#00aaff' },
      { name: 'Business', value: types.business, color: '#00ff88' },
      { name: 'Startup', value: types.startup, color: '#aa55ff' },
    ].filter(d => d.value > 0);
  }, [activeLoans]);

  if (data.length === 0) {
    return (
      <div className="glass-panel p-4 flex items-center justify-center" style={{ minHeight: 180 }}>
        <p className="text-gray-600 text-sm">No active loans</p>
      </div>
    );
  }

  return (
    <div className="glass-panel p-4">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Portfolio Mix</h3>
      <div className="flex items-center">
        <ResponsiveContainer width="55%" height={150}>
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={3} dataKey="value">
              {data.map((d, i) => <Cell key={i} fill={d.color} stroke="transparent" />)}
            </Pie>
            <Tooltip formatter={formatMoney} />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex-1 space-y-2">
          {data.map((d, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
              <span className="text-gray-400">{d.name}</span>
              <span className="font-mono text-gray-300 ml-auto">{formatMoney(d.value)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RiskChart() {
  const { monthlyHistory } = useGameStore();

  const data = monthlyHistory.map(m => ({
    month: m.month,
    risk: m.riskScore,
  }));

  if (data.length < 2) return null;

  return (
    <div className="glass-panel p-4">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Risk History</h3>
      <ResponsiveContainer width="100%" height={120}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="riskGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ffcc00" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#ffcc00" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
          <XAxis dataKey="month" stroke="#4b5563" tick={{ fontSize: 10 }} />
          <YAxis domain={[0, 100]} stroke="#4b5563" tick={{ fontSize: 10 }} />
          <Tooltip content={<CustomTooltip />} />
          <Area type="monotone" dataKey="risk" stroke="#ffcc00" fill="url(#riskGrad)" strokeWidth={2} name="Risk Score" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function Charts() {
  const { month } = useGameStore();

  if (month <= 1) {
    return (
      <div className="p-4">
        <div className="glass-panel p-8 text-center">
          <div className="text-4xl mb-3">📊</div>
          <h3 className="text-lg font-bold text-white mb-2">Dashboard</h3>
          <p className="text-sm text-gray-400">Charts will appear after your first month of operations.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 overflow-y-auto">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <CapitalChart />
        <ProfitLossChart />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <PortfolioPie />
        <RiskChart />
      </div>
    </div>
  );
}
